import type { ChildTicketTask } from '@/lib/services/childTicketService'
import { resolveTaskPanelType } from './taskUtils'

export type TaskSourceField = 'fe_visit' | 'tools_parts' | 'attachments'

export function matchesTaskSourceField(task: ChildTicketTask, sourceField: TaskSourceField): boolean {
  if (task.source_field === sourceField) return true

  switch (sourceField) {
    case 'fe_visit':
      return (
        ['fe_schedule', 'fe_visit', 'field_visit'].includes((task.action_type ?? '').trim()) ||
        resolveTaskPanelType(task) === 'fe'
      )
    case 'tools_parts':
      return (task.action_type ?? '').trim() === 'parts_procurement'
    case 'attachments':
      return (
        (task.action_type ?? '').trim() === 'document_collection' ||
        (task.action_type ?? '').trim() === 'work_permit' ||
        resolveTaskPanelType(task) === 'permit'
      )
    default:
      return false
  }
}

export function filterTasksBySourceField(
  tasks: ChildTicketTask[],
  sourceField: TaskSourceField,
): ChildTicketTask[] {
  return tasks.filter((task) => matchesTaskSourceField(task, sourceField))
}

export function findPrimaryTaskForSourceField(
  tasks: ChildTicketTask[],
  sourceField: TaskSourceField,
): ChildTicketTask | undefined {
  return filterTasksBySourceField(tasks, sourceField)[0]
}

export function countTasksBySourceField(tasks: ChildTicketTask[], sourceField: TaskSourceField): number {
  return filterTasksBySourceField(tasks, sourceField).length
}

export function formatSourceFieldCounter(sourceField: TaskSourceField, count: number): string | null {
  if (count <= 0) return null

  switch (sourceField) {
    case 'tools_parts':
      return `${count} procurement task${count === 1 ? '' : 's'}`
    case 'attachments':
      return `${count} document task${count === 1 ? '' : 's'}`
    default:
      return null
  }
}

export function defaultCreatePayloadForSourceField(sourceField: TaskSourceField): Partial<ChildTicketTask> {
  switch (sourceField) {
    case 'fe_visit':
      return { title: 'Field engineer visit', action_type: 'fe_schedule', source_field: 'fe_visit' }
    case 'tools_parts':
      return { title: 'Parts procurement', action_type: 'parts_procurement', source_field: 'tools_parts' }
    case 'attachments':
      return { title: 'Document collection', action_type: 'document_collection', source_field: 'attachments' }
    default:
      return {}
  }
}

/** Template code used when creating from a source-field badge via TaskDetailDrawer. */
export function templateCodeForSourceField(sourceField: TaskSourceField | string | null | undefined): string | null {
  if (sourceField === 'attachments') return 'document_collection'
  if (sourceField === 'tools_parts') return 'parts_procurement'
  return null
}

/** True when drawer create should resolve/create via a task template (not blank create). */
export function isTemplateBackedCreate(
  createDefaults: Partial<ChildTicketTask> | null | undefined,
): boolean {
  if (!createDefaults) return false
  if (createDefaults.source_field === 'attachments' || createDefaults.source_field === 'tools_parts') return true
  const action = (createDefaults.action_type ?? '').trim()
  if (action === 'document_collection' || action === 'parts_procurement') return true
  return false
}
